import { useQuery } from "@tanstack/vue-query";
import {
  type BidDetailsProduct,
  type ProductDetails,
  SalesMethod,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";

const useSortedBids = (
  product: MaybeRefOrGetter<BidDetailsProduct | ProductDetails | undefined>,
) => {
  const productId = computed(() => toValue(product)?.id);
  const isProductBidsQueryEnabled = computed(
    () =>
      toValue(product)?.salesMethod === SalesMethod.Bidding &&
      !!productId.value,
  );

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const { data: bids, isFetching: isBidsFetching } = useQuery({
    // `enabled` option below ensures the type assertions
    ...queries.products.bids(productId as Ref<string>, platformId),
    enabled: isProductBidsQueryEnabled,
    // refetch every minute - 60000ms
    refetchInterval: 60000,
    select: (bids) =>
      bids.sort(
        (a, b) => b.bidReceivedAt.getTime() - a.bidReceivedAt.getTime(),
      ),
  });

  const sortedBids = computed(() => {
    if (!toValue(product) || !bids.value) return [];

    return bids.value;
  });

  return {
    sortedBids,
    isBidsFetching,
  };
};

export const useProductBids = (
  product: MaybeRefOrGetter<BidDetailsProduct | ProductDetails>,
) => {
  const { isLoggedIn } = useAuth();
  const { userId } = useUser();
  const { sortedBids, isBidsFetching } = useSortedBids(product);

  const obfuscatedUserIdForProduct = ref<number | undefined>(undefined);
  watchEffect(async () => {
    if (!isLoggedIn.value || !userId.value) return;

    const obfuscatedUserId = await calculateObfuscatedUserId(
      userId.value,
      toValue(product).id,
    );

    obfuscatedUserIdForProduct.value = parseInt(obfuscatedUserId);
  });

  const userProductBids = computed(() => {
    if (!isLoggedIn.value || !sortedBids.value) return [];

    return sortedBids.value.filter(
      (bid) => bid.obfuscatedUserId === obfuscatedUserIdForProduct.value,
    );
  });

  const currentUserHasBid = computed(() => userProductBids.value.length > 0);

  const isCurrentUserTheHighestBidder = computed(() =>
    userProductBids.value.some((bid) => {
      if (!bid.prices) return false;

      return bid.highestBid;
    }),
  );

  const isWonByCurrentUser = computed(
    () =>
      isCurrentUserTheHighestBidder.value &&
      Boolean(toValue(product)?.finished),
  );

  return {
    isCurrentUserTheHighestBidder,
    isBidsFetching,
    isWonByCurrentUser,
    currentUserHasBid,
    sortedBids,
    obfuscatedUserIdForProduct,
  };
};
